import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { formatUsd } from '@/lib/format';
import { fmtMirrorDate } from '@/lib/dates';
import { useGroupAssets } from '@/hooks/useGroupAssets';
import {
  ALL_STORES,
  ChainPage,
  EmptyRow,
  Pill,
  StoreCell,
  StoreFilterRow,
  Tile,
} from '@/components/chain/ChainShell';

type Scope = 'on_site' | 'removed' | 'all';

const SCOPES: Array<{ id: Scope; label: string }> = [
  { id: 'on_site', label: 'On site' },
  { id: 'removed', label: 'Removed' },
  { id: 'all', label: 'Everything' },
];

/**
 * /equipment for a chain's corporate login — every store's equipment and
 * contracts in one table, with a Store column, instead of 26 context switches.
 */
export default function ChainEquipment() {
  const { data, isLoading } = useGroupAssets();
  const [scope, setScope] = useState<Scope>('on_site');
  const [store, setStore] = useState<string>(ALL_STORES);
  const [term, setTerm] = useState('');

  const rows = useMemo(() => {
    let out = data?.equipment ?? [];
    if (scope === 'on_site') out = out.filter((r) => r.on_site);
    if (scope === 'removed') out = out.filter((r) => !r.on_site);
    if (store !== ALL_STORES) out = out.filter((r) => r.customer_id === store);
    const q = term.trim().toLowerCase();
    if (q) {
      out = out.filter((r) =>
        [r.name, r.serial_number, r.asset_tag, r.model_number, r.make, r.store_name]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q)),
      );
    }
    return out;
  }, [data, scope, store, term]);

  const contracts = useMemo(() => {
    let out = data?.contracts ?? [];
    if (store !== ALL_STORES) out = out.filter((r) => r.customer_id === store);
    return out;
  }, [data, store]);

  const t = data?.totals;
  const brand = data?.master?.group_brand || data?.master?.name || 'your stores';
  const storesWithGear = new Set(
    (data?.equipment ?? []).filter((r) => r.on_site).map((r) => r.customer_id),
  ).size;

  return (
    <ChainPage
      title="Equipment — all stores"
      intro={
        <>
          Every piece of equipment we have on record across {brand}. The Store column
          tells you where it sits; nothing here changes which account you are in.
        </>
      }
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Tile label="On site" value={isLoading ? '—' : String(t?.equipment_on_site ?? 0)} />
        <Tile
          label="Monthly rent"
          value={isLoading ? '—' : formatUsd(t?.equipment_monthly_rent ?? 0)}
        />
        <Tile label="Contracts" value={isLoading ? '—' : String(t?.contracts ?? 0)} />
        <Tile
          label="Stores with equipment"
          value={isLoading ? '—' : String(storesWithGear)}
          sub={t ? `of ${t.stores} on the account` : undefined}
        />
      </div>

      <StoreFilterRow
        stores={data?.stores ?? []}
        store={store}
        onStore={setStore}
        term={term}
        onTerm={setTerm}
        searchPlaceholder="Serial, model, store…"
      >
        {SCOPES.map((s) => (
          <Pill key={s.id} active={scope === s.id} onClick={() => setScope(s.id)}>
            {s.label}
          </Pill>
        ))}
      </StoreFilterRow>

      <div className="rounded-2xl border border-brix-border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-brix-surface-alt/60 text-left text-[11px] uppercase tracking-wide text-brix-muted">
              <tr>
                <th className="px-4 py-3 font-semibold">Store</th>
                <th className="px-4 py-3 font-semibold">Equipment</th>
                <th className="px-4 py-3 font-semibold">Serial</th>
                <th className="px-4 py-3 font-semibold">Installed</th>
                <th className="px-4 py-3 font-semibold text-right">Monthly</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <EmptyRow colSpan={5}>
                  <Loader2 size={16} className="animate-spin inline-block mr-2" />
                  Loading every store's equipment…
                </EmptyRow>
              ) : rows.length === 0 ? (
                <EmptyRow colSpan={5}>
                  {(data?.equipment ?? []).length === 0
                    ? "We don't have equipment on record for these stores yet."
                    : 'Nothing matches those filters.'}
                </EmptyRow>
              ) : (
                rows.map((r) => (
                  <tr key={r.id} className="border-t border-brix-border/70">
                    <StoreCell row={r} />
                    <td className="px-4 py-3">
                      <span className="block font-medium text-brix-ink">{r.name}</span>
                      <span className="block text-[11px] text-brix-muted">
                        {[r.make, r.model_number].filter(Boolean).join(' · ') || r.category || '—'}
                        {r.is_loaner && ' · loaner'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-brix-muted tabular-nums">
                      {r.serial_number || r.asset_tag || '—'}
                    </td>
                    <td className="px-4 py-3 text-brix-muted">
                      {r.on_site
                        ? r.installed_at
                          ? fmtMirrorDate(r.installed_at)
                          : '—'
                        : `Removed ${r.removed_at ? fmtMirrorDate(r.removed_at) : ''}`.trim()}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-brix-ink">
                      {r.monthly_rent > 0 ? formatUsd(r.monthly_rent) : '—'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {contracts.length > 0 && (
        <div className="mt-8">
          <h2 className="font-display text-lg font-bold text-brix-ink mb-3">Contracts</h2>
          <div className="rounded-2xl border border-brix-border bg-white overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-brix-surface-alt/60 text-left text-[11px] uppercase tracking-wide text-brix-muted">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Store</th>
                    <th className="px-4 py-3 font-semibold">Contract</th>
                    <th className="px-4 py-3 font-semibold">Term</th>
                    <th className="px-4 py-3 font-semibold text-right">Monthly</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr key={c.id} className="border-t border-brix-border/70">
                      <StoreCell row={c} />
                      <td className="px-4 py-3">
                        <span className="block font-medium text-brix-ink">
                          {c.contract_number || 'Agreement'}
                        </span>
                        <span className="block text-[11px] text-brix-muted">
                          {[c.contract_type, c.status].filter(Boolean).join(' · ') || '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-brix-muted">
                        {c.start_date ? fmtMirrorDate(c.start_date) : '—'}
                        {c.end_date ? ` → ${fmtMirrorDate(c.end_date)}` : ''}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-brix-ink">
                        {formatUsd(c.monthly_equipment_amount + c.monthly_service_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <p className="mt-4 text-center text-xs text-brix-muted">
        Equipment we lease or service is listed here. Anything a store bought outright,
        or gear managed in the foodservice portal, won't appear — ask us and we'll
        confirm what's on your account.
      </p>
    </ChainPage>
  );
}
